import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { PendingTableCell } from "@/pages/employees/employee/tables/PendingTableCell.tsx";
import {
  createExpandableTextCell,
  createHistoricalDateCell,
  createTextCell,
} from "@/pages/employees/employee/tables/table-cell-factory.utils.tsx";
import { EmployeePositionEditModal } from "@/pages/employees/employee/widgets/EmployeePositionEditModal.tsx";
import {
  useEmployeePositionDelete,
  useEmployeePositionsSearch,
} from "@/queries/employees/use-employee-positions.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiEmployeePosition,
  ApiEmployeePositionPendingRow,
  getEmployeePositionTitle,
} from "@/types/employees/employee-position.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type PositionRow = (ApiEmployeePosition & { pending?: never }) | ApiEmployeePositionPendingRow;

export const EmployeeWorkInfoPosition = ({
  employee,
  tableScrollBoundaryRef,
}: EmployeePageComponentProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { table, fields } = useSystemTableConfig(ApiTableTypes.employee_positions, "view");
  const { renderFieldDataInCell } = useCustomFieldsHook();
  const {
    canViewField,
    canEditAnyOfFields,
    canViewAnyOfFields,
    canEditField,
    canViewCustomTableFieldForEmployee,
  } = usePermissions();
  const {
    renderCurrency,
    renderDate,
    renderEmployeeName,
    formatDurationPartialDate,
    transformDateToPartialDate,
  } = useFormatter();

  const [page, setPage] = useState(1);

  const canViewHistory = useMemo(
    () => canViewAnyOfFields(employee, [EmployeeFieldPermission.employee_positions_history]),
    [employee, canViewAnyOfFields]
  );

  const canManagePositions = useMemo(
    () => canEditAnyOfFields(employee, [EmployeeFieldPermission.manage_employee_position]),
    [employee, canEditAnyOfFields]
  );

  const {
    data: employeePositions,
    isLoading,
    isFetching,
  } = useEmployeePositionsSearch(employee.id, page, canViewHistory);

  const employeePositionDelete = useEmployeePositionDelete();
  const currentPositionId = employee.employee_position?.id;

  const isCurrentPosition = useCallback(
    (position: PositionRow) => !position.pending && position.id === currentPositionId,
    [currentPositionId]
  );

  const getTextClassName = useCallback(
    (position: PositionRow) => (isCurrentPosition(position) ? "text-title" : "text-secondary"),
    [isCurrentPosition]
  );

  const handleModifyPosition = useCallback(
    (employeePosition?: ApiEmployeePosition) => {
      openModal(EmployeePositionEditModal, {
        employee,
        employeePosition,
      });
    },
    [employee, openModal]
  );

  const handleDeleteAction = useCallback(
    (employeePosition: ApiEmployeePosition) => {
      if (!canEditField(employee, EmployeeFieldPermission.manage_employee_position)) return;

      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          employeePositionDelete.mutate(
            { employeeId: employee.id, positionId: employeePosition.id },
            {
              onSuccess: () => {
                analyticsService.trackEvent(analyticEvents.employeePositions.deleted, {
                  [analyticProperties.id]: employee.id,
                });
              },
            }
          );
        },
      });
    },
    [employee, canEditField, openModal, employeePositionDelete, t]
  );

  const handleEdit = useCallback(
    (employeePosition: ApiEmployeePosition) => {
      if (!canEditField(employee, EmployeeFieldPermission.manage_employee_position)) return;

      openModal(EmployeePositionEditModal, {
        employee,
        employeePosition,
      });
    },
    [employee, canEditField, openModal]
  );

  const displayData = useMemo<PositionRow[]>(
    () => [
      ...(page === 1 ? (employeePositions?.pending_rows ?? []) : []),
      ...(employeePositions?.items ?? []),
    ],
    [employeePositions, page]
  );

  const columnHelper = createColumnHelper<PositionRow>();
  const columns = useMemo<ColumnDef<PositionRow, unknown>[]>(() => {
    const inner: ColumnDef<PositionRow, unknown>[] = [];

    if (canViewField(employee, EmployeeFieldPermission.employee_position_date)) {
      inner.push(
        columnHelper.display({
          header: t("common:date"),
          cell: (cell) => {
            const row = cell.row.original;
            if (row.pending) {
              return (
                <PendingTableCell pending={row.pending}>
                  {row.date_effective_from ? renderDate(row.date_effective_from) : "-"}
                </PendingTableCell>
              );
            }
            return createHistoricalDateCell<PositionRow>({
              getValue: (pos) =>
                pos.date_effective_from ? renderDate(pos.date_effective_from) : null,
              getClassName: getTextClassName,
              getDate: (pos) => pos.date_effective_from,
            })(cell);
          },
          id: ApiFieldTypes.effective_from,
          ...ColumnSizes.historicalDate,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_job_title)) {
      inner.push(
        columnHelper.display({
          header: t("core:job_profile"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => (pos.job_profile ? getJobProfileTitle(pos.job_profile) : null),
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_job_profile,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_reporting_to)) {
      inner.push(
        columnHelper.display({
          header: t("core:manager"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => (pos.manager ? renderEmployeeName(pos.manager) : null),
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_reporting_to,
          ...ColumnSizes.employee,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_department)) {
      inner.push(
        columnHelper.display({
          header: t("core:department"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => pos.department?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_department,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_division)) {
      inner.push(
        columnHelper.display({
          header: t("core:division"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => pos.division?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_division,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_location)) {
      inner.push(
        columnHelper.display({
          header: t("common:location"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => pos.location?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_location,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_comment)) {
      inner.push(
        columnHelper.display({
          header: t("common:comments"),
          cell: createExpandableTextCell<PositionRow>({
            getValue: (pos) => pos.comment,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_comment,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_positions_history)) {
      inner.push(
        columnHelper.display({
          header: t("core:employee_position_change_reason"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => pos.change_reason?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_position_change_reason,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_position_date)) {
      inner.push(
        columnHelper.display({
          header: t("common:date_effective_to"),
          cell: createTextCell<PositionRow>({
            getValue: (pos) => (pos.date_effective_to ? renderDate(pos.date_effective_to) : null),
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.effective_to,
          ...ColumnSizes.standard,
        })
      );
    }

    const configColumns: ColumnDef<PositionRow, unknown>[] = fields.flatMap((field) => {
      if (field.type === ApiFieldTypes.custom) {
        if (!table || !canViewCustomTableFieldForEmployee(employee, table.id, field.slug)) {
          return [];
        }
        return [
          columnHelper.display({
            header: field.title,
            cell: ({ row }) => {
              const customFields =
                "custom_fields" in row.original ? row.original.custom_fields : [];
              return renderFieldDataInCell(field, customFields ?? []);
            },
            id: field.slug,
            ...ColumnSizes.standard,
          }),
        ];
      }

      if (
        field.type === ApiFieldTypes.changed_by &&
        canViewField(employee, EmployeeFieldPermission.employee_position_changed_by)
      ) {
        return [
          columnHelper.display({
            header: field.title?.trim() || t("core:changed_by"),
            cell: ({ row }) =>
              row.original.changed_by ? (
                <EmployeeHoverCard
                  employee={row.original.changed_by}
                  position={getEmployeePositionTitle(row.original.changed_by)}
                >
                  <EmployeeItem employee={row.original.changed_by} showPosition={false} size="sm" />
                </EmployeeHoverCard>
              ) : null,
            id: field.type,
            ...ColumnSizes.standard,
          }),
        ];
      }

      const column = inner.find((item) => item.id === field.type);
      const fallbackTitle = typeof column?.header === "string" ? column.header : "";
      return column ? [{ ...column, header: field.title?.trim() || fallbackTitle }] : [];
    });

    return configColumns;
  }, [
    employee,
    canViewField,
    canManagePositions,
    getTextClassName,
    renderDate,
    renderCurrency,
    renderEmployeeName,
    handleEdit,
    handleDeleteAction,
    employeePositionDelete.isPending,
    t,
    fields,
    table,
    canViewCustomTableFieldForEmployee,
    renderFieldDataInCell,
  ]);

  const renderRowAction = useCallback(
    (row: Row<PositionRow>) => {
      if (row.original.pending) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => handleEdit(row.original as ApiEmployeePosition)}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => handleDeleteAction(row.original as ApiEmployeePosition)}
            variant="menuDanger"
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [handleEdit, handleDeleteAction, t]
  );

  const hiredOn = useMemo(() => {
    if (!employee.date_hired_on || employee.date_hired_on > new Date()) return undefined;
    if (employee.lifecycle_status === ApiEmployeeLifecycleStatus.terminated) return undefined;

    return t("core:employee_is_member_of_team_template", {
      employee_name: renderEmployeeName(employee),
      period: formatDurationPartialDate(
        transformDateToPartialDate(employee.date_hired_on),
        undefined
      ),
    });
  }, [employee, t, renderEmployeeName, formatDurationPartialDate, transformDateToPartialDate]);

  const displayCount = useMemo(() => {
    if (!canViewHistory) return employee.employee_position ? 1 : 0;
    return employeePositions?.metadata.total_items || 0;
  }, [canViewHistory, employee.employee_position, employeePositions?.metadata.total_items]);

  return (
    <Stack gap="lg">
      <Stack
        className="justify-between"
        direction="horizontal"
        items="center"
        gap="md"
        data-employee-table-heading
      >
        <Title
          header={t("core:position")}
          size="lg"
          caption={hiredOn}
          badge={displayCount > 1 ? `(${displayCount})` : undefined}
          badgeVariant={"secondary"}
        />
        <Stack direction="horizontal" gap={"sm"}>
          {canEditField(employee, EmployeeFieldPermission.manage_employee_position) && (
            <Button
              onClick={() => handleModifyPosition()}
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:add_record")}
            </Button>
          )}
        </Stack>
      </Stack>

      <HistoryTable<PositionRow>
        currentItem={employee.employee_position ?? undefined}
        historyItems={displayData}
        columns={columns}
        canViewHistory={canViewHistory}
        isLoading={isLoading}
        isFetching={isFetching || employeePositionDelete.isPending}
        pagination={{
          metadata: employeePositions?.metadata,
          currentPage: page,
          onPageChange: setPage,
        }}
        emptyStateTitle={t("core:no_position_assigned")}
        scrollLayout="notion"
        scrollBoundaryRef={tableScrollBoundaryRef}
        stickyLastColumn={false}
        renderRowAction={canManagePositions ? renderRowAction : undefined}
      />
    </Stack>
  );
};
